import React, { useMemo } from "react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const COLORS = ["#10b981", "#3b82f6", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899"];

const PerformanceDashboard = ({ reservations, period }) => {
  // Calcul des données pour les graphiques
  const chartData = useMemo(() => {
    if (!reservations || reservations.length === 0) {
      return {
        revenueByDay: [],
        profitByDay: [],
        reservationsByStatus: [],
        topDestinations: [],
        marginTrend: [],
      };
    }

    // Filtrer les réservations selon la période
    const now = new Date();
    const filtered = reservations.filter((r) => {
      const date = new Date(r.created_at);
      switch (period) {
        case "today":
          return date.toDateString() === now.toDateString();
        case "7days":
          return (now - date) / (1000 * 60 * 60 * 24) <= 7;
        case "30days":
          return (now - date) / (1000 * 60 * 60 * 24) <= 30;
        case "thisMonth":
          return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
        case "year":
          return date.getFullYear() === now.getFullYear();
        default:
          return true;
      }
    });

    // Revenus et bénéfices par jour
    const revenueMap = {};
    const profitMap = {};
    filtered.forEach((r) => {
      // Ne compter que les paiements réussis
      if (r.payment_status !== "success") return;
      
      const date = new Date(r.created_at).toLocaleDateString("fr-FR", {
        day: "2-digit",
        month: "short",
      });
      const revenue = parseFloat(r.payment_amount) || 0;
      let cost = 0;
      try {
        const flightData =
          typeof r.flight_data === "string" ? JSON.parse(r.flight_data) : r.flight_data;
        cost = parseFloat(flightData?.[0]?.price?.total) || 0;
      } catch (e) {
        // Ignore
      }
      const profit = revenue - cost;

      revenueMap[date] = (revenueMap[date] || 0) + revenue;
      profitMap[date] = (profitMap[date] || 0) + profit;
    });

    // Trier par date réelle et prendre les dernières entrées
    const sortedDates = Object.keys(revenueMap).sort((a, b) => {
      const dateA = new Date(a.split(' ')[0] + ' ' + a.split(' ')[1] + ' ' + now.getFullYear());
      const dateB = new Date(b.split(' ')[0] + ' ' + b.split(' ')[1] + ' ' + now.getFullYear());
      return dateA - dateB;
    });
    
    const revenueByDay = sortedDates
      .slice(-15) // Afficher les 15 derniers jours avec données
      .map((date) => ({
        date,
        revenus: Math.round(revenueMap[date]),
        benefices: Math.round(profitMap[date]),
      }));

    // Réservations par statut
    const statusMap = {};
    filtered.forEach((r) => {
      statusMap[r.reservation_status] = (statusMap[r.reservation_status] || 0) + 1;
    });

    const statusLabels = {
      en_attente: "En attente",
      en_cours: "En cours",
      confirmée: "Confirmée",
      terminée: "Terminée",
    };

    const reservationsByStatus = Object.keys(statusMap).map((status) => ({
      name: statusLabels[status] || status,
      value: statusMap[status],
    }));

    // Top destinations
    const destinationMap = {};
    filtered.forEach((r) => {
      try {
        const flightData =
          typeof r.flight_data === "string" ? JSON.parse(r.flight_data) : r.flight_data;
        const destination =
          flightData?.[0]?.itineraries?.[0]?.segments?.slice(-1)[0]?.arrival?.iataCode;
        if (destination) {
          destinationMap[destination] = (destinationMap[destination] || 0) + 1;
        }
      } catch (e) {
        // Ignore
      }
    });

    const topDestinations = Object.keys(destinationMap)
      .map((dest) => ({
        destination: dest,
        reservations: destinationMap[dest],
      }))
      .sort((a, b) => b.reservations - a.reservations)
      .slice(0, 5);

    // Évolution des marges
    const marginByDay = sortedDates
      .slice(-15) // Afficher les 15 derniers jours avec données
      .map((date) => ({
        date,
        marge:
          revenueMap[date] > 0
            ? Math.round(((profitMap[date] / revenueMap[date]) * 100) * 10) / 10
            : 0,
      }));

    return {
      revenueByDay,
      reservationsByStatus,
      topDestinations,
      marginTrend: marginByDay,
    };
  }, [reservations, period]);

  // Calcul des statistiques globales
  const globalStats = useMemo(() => {
    const totalRevenue = chartData.revenueByDay.reduce((sum, day) => sum + day.revenus, 0);
    const totalProfit = chartData.revenueByDay.reduce((sum, day) => sum + day.benefices, 0);
    const avgMargin = totalRevenue > 0 ? ((totalProfit / totalRevenue) * 100).toFixed(1) : 0;
    const totalReservations = chartData.reservationsByStatus.reduce((sum, s) => sum + s.value, 0);
    
    return { totalRevenue, totalProfit, avgMargin, totalReservations };
  }, [chartData]);

  return (
    <div className="space-y-6">
      {/* Statistiques récapitulatives */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg shadow p-4 border-l-4 border-green-500">
          <p className="text-sm text-gray-600 font-medium">Revenus Totaux</p>
          <p className="text-2xl font-bold text-green-700">{globalStats.totalRevenue.toLocaleString()} XOF</p>
        </div>
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg shadow p-4 border-l-4 border-blue-500">
          <p className="text-sm text-gray-600 font-medium">Bénéfices Totaux</p>
          <p className="text-2xl font-bold text-blue-700">{globalStats.totalProfit.toLocaleString()} XOF</p>
        </div>
        <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg shadow p-4 border-l-4 border-orange-500">
          <p className="text-sm text-gray-600 font-medium">Marge Moyenne</p>
          <p className="text-2xl font-bold text-orange-700">{globalStats.avgMargin}%</p>
        </div>
        <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg shadow p-4 border-l-4 border-purple-500">
          <p className="text-sm text-gray-600 font-medium">Réservations</p>
          <p className="text-2xl font-bold text-purple-700">{globalStats.totalReservations}</p>
        </div>
      </div>

      {/* Revenus et Bénéfices */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">
          📊 Revenus et Bénéfices sur la période
        </h3>
        {chartData.revenueByDay.length === 0 ? (
          <div className="flex items-center justify-center h-64 text-gray-400">
            <p>Aucune donnée disponible pour cette période</p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData.revenueByDay}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="date" 
                angle={-45}
                textAnchor="end"
                height={80}
                style={{ fontSize: '12px' }}
              />
              <YAxis 
                tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`}
                style={{ fontSize: '12px' }}
              />
              <Tooltip
                formatter={(value) => `${value.toLocaleString()} XOF`}
                labelStyle={{ color: "#000" }}
                contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', border: '1px solid #ccc' }}
              />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="revenus" 
                stroke="#10b981" 
                strokeWidth={3}
                name="Revenus clients"
                dot={{ fill: '#10b981', r: 4 }}
              />
              <Line 
                type="monotone" 
                dataKey="benefices" 
                stroke="#3b82f6" 
                strokeWidth={3}
                name="Bénéfices nets"
                dot={{ fill: '#3b82f6', r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Réservations par statut */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">📋 Répartition par statut</h3>
          {chartData.reservationsByStatus.length === 0 ? (
            <div className="flex items-center justify-center h-64 text-gray-400">
              <p>Aucune réservation</p>
            </div>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={chartData.reservationsByStatus}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent, value }) => 
                      `${name}: ${value} (${(percent * 100).toFixed(0)}%)`
                    }
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {chartData.reservationsByStatus.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => `${value} réservation(s)`} />
                </PieChart>
              </ResponsiveContainer>
              {/* Légende avec détails */}
              <div className="mt-4 grid grid-cols-2 gap-2 text-sm">
                {chartData.reservationsByStatus.map((item, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <div 
                      className="w-3 h-3 rounded-full" 
                      style={{ backgroundColor: COLORS[index % COLORS.length] }}
                    />
                    <span className="font-medium">{item.name}: {item.value}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Top destinations */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">✈️ Top 5 destinations</h3>
          {chartData.topDestinations.length === 0 ? (
            <div className="flex items-center justify-center h-64 text-gray-400">
              <p>Aucune destination</p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={chartData.topDestinations}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="destination" 
                  style={{ fontSize: '12px', fontWeight: 'bold' }}
                />
                <YAxis allowDecimals={false} />
                <Tooltip 
                  formatter={(value) => `${value} réservation(s)`}
                  contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', border: '1px solid #ccc' }}
                />
                <Bar 
                  dataKey="reservations" 
                  fill="#10b981"
                  radius={[8, 8, 0, 0]}
                  label={{ position: 'top', style: { fontWeight: 'bold' } }}
                />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Évolution des marges */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">📈 Évolution de la marge bénéficiaire (%)</h3>
        {chartData.marginTrend.length === 0 ? (
          <div className="flex items-center justify-center h-64 text-gray-400">
            <p>Aucune donnée de marge disponible</p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={chartData.marginTrend}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="date" 
                angle={-45}
                textAnchor="end"
                height={80}
                style={{ fontSize: '12px' }}
              />
              <YAxis 
                tickFormatter={(value) => `${value}%`}
                style={{ fontSize: '12px' }}
              />
              <Tooltip 
                formatter={(value) => `${value}%`} 
                labelStyle={{ color: "#000" }}
                contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', border: '1px solid #ccc' }}
              />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="marge" 
                stroke="#f59e0b" 
                strokeWidth={3}
                name="Marge bénéficiaire"
                dot={{ fill: '#f59e0b', r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

export default PerformanceDashboard;
